import type { WynikOdczytuRozjazdu } from '@/lib/machines-api';
import type { StanWdrozenia } from '@/lib/wdrozenie-api';
import { grupyTresci, opisRozjazdu } from '@/lib/rozjazd-opis';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  PLIK Z ROZJAZDAMI — to, co portal ZMIERZYŁ, do wyniesienia z portalu.
 *
 *  ⚠️⚠️ ZAMÓWIONE 09.09.2026: „czy możemy tutaj zrobić plik z dokładnymi znalezionymi rozjazdami?".
 *  Okno pokazuje wszystko, ale na ekranie — a rozjazd przy wdrożeniu rozstrzyga się w rozmowie
 *  z BillVisem albo z serwisem klienta, czyli poza portalem.
 *
 *  ⚠️⚠️ PLIK MÓWI TEŻ, CZEGO NIE WIE, i to jest jego najważniejsza część. Portal NIE MA dostępu
 *  do wierszy baz klienta: czujnik liczy WIERSZE per automat i skróty treści (agent,
 *  `sync/geometria.ts`). Więc „dokładne rozjazdy" znaczy: która tabela, który automat, który
 *  węzeł, ile wierszy i czy skrót treści się różni — a NIE „te trzy wiersze się różnią".
 *  Bez tego zdania plik wyglądałby na raport z porównania rekordów i tak byłby czytany.
 *
 *  ⚠️⚠️ SKŁADAMY GO Z TEJ SAMEJ ODPOWIEDZI, KTÓRĄ RYSUJE OKNO — nie z drugiego odczytu. Plik
 *  i ekran muszą mówić dokładnie to samo; drugie zapytanie znaczyłoby, że mogą się rozjechać
 *  między jednym a drugim cyklem agenta, a wtedy człowiek ma dwie prawdy i żadnej.
 *
 *  ⚠️ ZWYKŁY TEKST, NIE CSV. Ten plik się CZYTA i wysyła dalej: ma nagłówek z kontekstem
 *  (wyspa, chwila pomiaru, co jest czym) i drzewo automat → tabela → węzły. CSV wymuszałby
 *  jeden płaski wiersz na węzeł i wyrzucałby właśnie ten kontekst.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

export type DaneRaportu = Pick<StanWdrozenia, 'rozjazdy' | 'automaty' | 'doWyrownania' | 'tabeleWyrownania'> & {
  wyspa: Pick<StanWdrozenia['wyspa'], 'name' | 'code'>;
};

/** Nazwa pliku: wyspa i chwila pomiaru — dwa pytania, które padają przy trzecim takim pliku. */
export function nazwaPlikuRozjazdu(kodWyspy: string, kiedy: Date): string {
  const dwa = (n: number): string => String(n).padStart(2, '0');
  const stempel =
    `${kiedy.getFullYear()}${dwa(kiedy.getMonth() + 1)}${dwa(kiedy.getDate())}` +
    `-${dwa(kiedy.getHours())}${dwa(kiedy.getMinutes())}`;
  const kod = kodWyspy.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'wyspa';
  return `rozjazd-${kod}-${stempel}.txt`;
}

const KRESKA = '─'.repeat(78);

/**
 * ═════════════════════════════════════════════════════════════════════════════
 *  ODCZYTY Z BAZ — klucz `vmid|tabela`, dokładnie ten sam, którym okno trzyma swoje wyniki.
 *
 *  ⚠️⚠️ BEZ TEGO PLIK KŁAMAŁBY. Okno przy uciętej liście pisze „pełna w pliku" — a plik znał
 *  wtedy wyłącznie liczby i skróty z czujnika. Obietnica bez pokrycia w pliku, który się wysyła
 *  dalej, jest gorsza od braku obietnicy: adresat nie ma jak sprawdzić, że czegoś nie dostał.
 *
 *  ⚠️ ODCZYT JEST OPCJONALNY. Plik zapisany PRZED odpytaniem baz nadal ma sens (liczby, skróty,
 *  co blokuje) — tylko bez sekcji wierszy, i mówi to wprost.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export type Odczyty = Readonly<Record<string, WynikOdczytuRozjazdu>>;

export function raportRozjazdu(d: DaneRaportu, kiedy: Date, odczyty: Odczyty = {}): string {
  const linie: string[] = [];
  const poAutomacie = new Map<number, StanWdrozenia['rozjazdy']>();
  for (const r of d.rozjazdy) {
    const lista = poAutomacie.get(r.vmid);
    if (lista === undefined) poAutomacie.set(r.vmid, [r]);
    else lista.push(r);
  }
  const vmidy = [...poAutomacie.keys()].sort((a, b) => a - b);
  const wyrownywalne = (tabela: string): boolean => d.tabeleWyrownania?.includes(tabela) ?? true;

  linie.push(`ROZJAZD KONFIGURACJI — wyspa „${d.wyspa.name}" (${d.wyspa.code})`);
  linie.push(`Zmierzono: ${kiedy.toLocaleString('pl-PL')} (czas przeglądarki)`);
  linie.push(`Tabel z rozjazdem: ${d.rozjazdy.length} · automatów: ${vmidy.length}`);
  linie.push('');
  linie.push('CO TO JEST, A CZEGO TU NIE MA');
  /**
   * ⚠️⚠️ TE CZTERY ZDANIA SĄ CZĘŚCIĄ PLIKU, NIE OZDOBĄ. Plik pójdzie do kogoś, kto nie siedzi
   * w tym portalu — a bez nich „brakuje 6 u chmura-mssql" czyta się jako „sprawdziliśmy wiersze".
   */
  linie.push('  • Portal liczy WIERSZE per automat i skrót treści tabeli — nie ma dostępu do samych');
  linie.push('    wierszy baz klienta, więc plik nie mówi, KTÓRE wiersze się różnią.');
  linie.push('  • „inna treść" = ta sama liczba wierszy, inny skrót. Nazwane kolumny to te, których');
  linie.push('    skróty się różnią; stan magazynowy i znaczniki czasu są z porównania wyłączone.');
  linie.push('  • „w drodze" = różnica jest świeża i może być zwykłą propagacją zmiany.');
  linie.push('  • „poza wyrównaniem" = tej tabeli nie rusza ani „Uzupełnij ten automat w innych bazach",');
  linie.push('    ani „Nadpisz ten automat w innych bazach" — te przyciski dotyczą geometrii bębna.');
  linie.push('');

  for (const vmid of vmidy) {
    const a = d.automaty?.find((x) => x.vmid === vmid);
    linie.push(KRESKA);
    linie.push(`AUTOMAT ${a?.nazwa ?? '(nazwy nie znamy)'} · VMID ${vmid}`);
    linie.push(
      `  właściciel (węzeł, na którym ten automat stoi): ${a?.wlascicielWezel ?? 'nieznany portalowi'}`,
    );
    /** ⚠️ `doWyrownania` = da się dosypać słabszym trybem; bez tego plik nie mówi, co zrobić. */
    if (d.doWyrownania?.includes(vmid) === true) {
      linie.push('  brak CAŁKOWITY wierszy u rodzeństwa → naprawia „Uzupełnij ten automat w innych bazach"');
    }
    linie.push('');

    for (const r of poAutomacie.get(vmid)!) {
      const o = opisRozjazdu(r.wezly, r.rodzaj, a?.wlascicielWezel ?? null);
      const znaczniki = [!r.stabilny ? 'w drodze' : null, wyrownywalne(r.tabela) ? null : 'poza wyrównaniem']
        .filter((x): x is string => x !== null)
        .join(', ');
      linie.push(`  ${r.tabela}${znaczniki === '' ? '' : `  [${znaczniki}]`}`);

      if (o.rodzaj === 'TRESC') {
        linie.push('    ta sama liczba wierszy, INNA ZAWARTOŚĆ');
        if (r.kolumny !== undefined && r.kolumny.length > 0) {
          linie.push(`    różne kolumny: ${r.kolumny.join(', ')}`);
        } else if (r.innyUklad === true) {
          linie.push('    kolumny zgodne, inny UKŁAD wierszy (te same wartości w innych przegródkach)');
        } else {
          linie.push('    kolumn nie znamy (starszy agent albo prośba o skróty jest świeża)');
        }
        for (const g of grupyTresci(r.wezly)) {
          const wlasciciel = a?.wlascicielWezel != null && g.wezly.includes(a.wlascicielWezel) ? ' ← właściciel' : '';
          linie.push(
            `    ${g.skrot === null ? 'bez skrótu' : g.skrot.slice(0, 12)}: ${g.wezly.join(', ')}${wlasciciel}`,
          );
        }
      } else {
        if (o.zrodlo !== null) linie.push(`    pełny obraz: ${o.zrodlo.nodeId} (${o.zrodlo.wierszy} wierszy)`);
        for (const g of o.grupy) {
          linie.push(
            g.wcale
              ? `    NIE MA ANI JEDNEGO WIERSZA: ${g.wezly.join(', ')}`
              : `    brakuje ${g.brakuje}: ${g.wezly.join(', ')}`,
          );
        }
        for (const n of o.nadmiar) linie.push(`    ${n.ponad} ponad właściciela: ${n.nodeId}`);
      }

      /** ⚠️ Wszystkie liczby, zawsze — to one są dowodem, a nie nasze podsumowanie. */
      const posortowane = [...r.wezly].sort((x, y) => y.wierszy - x.wierszy || x.nodeId.localeCompare(y.nodeId));
      for (const w of posortowane) linie.push(`      ${w.nodeId}: ${w.wierszy}`);

      /**
       * ⚠️⚠️ WIERSZE Z ODPYTANIA BAZ — tu jest odpowiedź na „których jedenastu". Bez odczytu
       * plik ma same liczby i mówi to wprost, zamiast milczeć — milczenie czytałoby się jako
       * „nie ma różnic w wierszach".
       */
      const zBaz = odczyty[`${vmid}|${r.tabela}`];
      if (zBaz === undefined || zBaz.stan !== 'GOTOWE') {
        linie.push('    (bazy nieodpytane — kliknij „Odpytaj bazy" w oknie rozjazdu, żeby dostać wiersze)');
      } else {
        linie.push(
          `    ODCZYT Z BAZ: ${zBaz.wezly
            .map((w) => `${w.nodeId}=${w.blad !== null ? 'BŁĄD' : `${w.wierszy ?? 0}${w.obcietych > 0 ? '+' : ''}`}`)
            .join(' ')}`,
        );
        for (const w of zBaz.wezly.filter((x) => x.blad !== null)) {
          linie.push(`      ${w.nodeId}: ${w.blad ?? ''}`);
        }
        if (zBaz.brakujace.length === 0 && zBaz.rozne.length === 0) {
          linie.push(`    wiersze zgodne we wszystkich bazach (${zBaz.kluczyRazem} kluczy)`);
        }
        for (const b of zBaz.brakujace) {
          linie.push(`    BRAK u ${b.nieMaja.join(', ')} — klucz ${b.klucz}`);
          for (const [k, v] of Object.entries(b.wartosci)) linie.push(`        ${k} = ${v ?? 'NULL'}`);
        }
        for (const x of zBaz.rozne) {
          linie.push(`    INNA TREŚĆ — klucz ${x.klucz}`);
          for (const kol of x.kolumny) {
            for (const g of kol.grupy) {
              linie.push(`        ${kol.kolumna} = ${g.wartosc ?? 'NULL'}  (${g.wezly.join(', ')})`);
            }
          }
        }
      }
      linie.push('');
    }
  }

  /**
   * ⚠️⚠️ PODSUMOWANIE NA KOŃCU ODPOWIADA NA PYTANIE, PO KTÓRE SIĘ TU PRZYSZŁO: co blokuje start
   * rozsyłania, a co nie. Do 09.09.2026 blokowało JEDNO I DRUGIE — także tabele, których żaden
   * przycisk portalu nie rusza.
   */
  const stabilne = d.rozjazdy.filter((r) => r.stabilny).map((r) => r.tabela);
  const unikalne = [...new Set(stabilne)];
  const blokujace = unikalne.filter((t) => wyrownywalne(t));
  const poza = unikalne.filter((t) => !wyrownywalne(t));
  linie.push(KRESKA);
  linie.push('CO Z TEGO WYNIKA DLA WŁĄCZENIA ROZSYŁANIA');
  linie.push(
    blokujace.length === 0
      ? '  Nic nie blokuje: w tabelach objętych wyrównaniem nie ma stabilnego rozjazdu.'
      : `  BLOKUJE: ${blokujace.join(', ')} — do wyprostowania „Nadpisz ten automat w innych bazach".`,
  );
  if (poza.length > 0) {
    linie.push(`  NIE BLOKUJE (poza wyrównaniem): ${poza.join(', ')}.`);
    linie.push('  Te tabele prostuje zwykła droga config-downu — ekrany, które je posiadają.');
  }
  linie.push('');

  return linie.join('\r\n');
}
